// wp/ACTIONS: a task comment's own controls, gated like a chat message's: Pin / Unpin for anyone
// who may post in the thread (the server's rule: the thread's posters), Edit for the author within
// chat's 15-minute window, Delete (asks first) for the author. Each is a visible icon on the
// message's hover bar, and the same three stay in its "…" menu. The thread message routes
// (`messages.pin` / `update` / `delete`) do the work; a refusal is the shared problem toast.
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  type ChatMessage,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  Textarea,
  toast,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PencilIcon, PinIcon, PinOffIcon, Trash2Icon } from "lucide-react";
import { type FormEvent, type ReactNode, useState } from "react";
import type { Api } from "../../api/client";
import { canEdit } from "../chat/chat-model";
import { patchThreadMessage } from "./task-cache";
import { workKeys } from "./work-queries";

type Row = Awaited<ReturnType<Api["client"]["messages"]["list"]>>["data"][number];

export function useCommentActions(
  api: Api,
  co: string,
  taskId: string,
  o: { rows: readonly Row[]; meId: string | null; now?: Date },
): {
  listProps: {
    currentUserId: string | undefined;
    onPinChange: (id: string, pinned: boolean) => void;
    onEdit: (id: string) => void;
    onDelete: (id: string) => void;
    extraActions: (m: ChatMessage) => ReactNode;
  };
  editable: (row: Row) => boolean;
  dialogs: ReactNode;
} {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Row | null>(null);
  const [deleting, setDeleting] = useState<Row | null>(null);
  const byId = new Map(o.rows.map((r) => [r.id, r]));
  const settle = () => void qc.invalidateQueries({ queryKey: workKeys.thread(co, taskId) });
  const pin = useMutation({
    mutationFn: (v: { row: Row; pinned: boolean }) =>
      api.client.messages.pin({
        params: { co, id: v.row.thread_id, mid: v.row.id },
        body: { pinned: v.pinned },
      }),
    onSuccess: (m, v) => {
      patchThreadMessage(qc, co, m as Row);
      toast.add({ title: v.pinned ? "Pinned" : "Unpinned", type: "success" });
    },
    onSettled: settle,
  });
  const edit = useMutation({
    mutationFn: (v: { row: Row; markdown: string }) =>
      api.client.messages.update({
        params: { co, id: v.row.thread_id, mid: v.row.id },
        body: { body_markdown: v.markdown },
      }),
    onSuccess: (m) => patchThreadMessage(qc, co, m as Row),
    onSettled: settle,
  });
  const remove = useMutation({
    mutationFn: (row: Row) =>
      api.client.messages.delete({ params: { co, id: row.thread_id, mid: row.id } }),
    onSuccess: () => toast.add({ title: "Message deleted", type: "success" }),
    onSettled: settle,
  });

  const now = o.now ?? new Date();
  const editable = (row: Row) => canEdit(row, o.meId, now);
  const mine = (row: Row) => o.meId !== null && row.author_membership_id === o.meId;
  const live = (row: Row | undefined): row is Row => Boolean(row && !row.deleted_at);

  const icon = (label: string, tip: string, Icon: typeof PinIcon, onClick: () => void) => (
    <Button
      key={tip}
      variant="ghost"
      size="icon-xs"
      aria-label={label}
      title={tip}
      className={tip === "Delete" ? "hover:text-destructive" : undefined}
      onClick={onClick}
    >
      <Icon aria-hidden="true" />
    </Button>
  );
  const extraActions = (m: ChatMessage) => {
    const row = byId.get(m.id);
    if (!live(row)) return null;
    return (
      <>
        {row.pinned_at
          ? icon("Unpin the message", "Unpin", PinOffIcon, () => pin.mutate({ row, pinned: false }))
          : icon("Pin the message", "Pin", PinIcon, () => pin.mutate({ row, pinned: true }))}
        {editable(row) ? icon("Edit the message", "Edit", PencilIcon, () => setEditing(row)) : null}
        {mine(row)
          ? icon("Delete the message", "Delete", Trash2Icon, () => setDeleting(row))
          : null}
      </>
    );
  };

  const listProps = {
    currentUserId: o.meId ?? undefined,
    onPinChange: (id: string, pinned: boolean) => {
      const row = byId.get(id);
      if (live(row)) pin.mutate({ row, pinned });
    },
    onEdit: (id: string) => {
      const row = byId.get(id);
      if (live(row)) setEditing(row);
    },
    onDelete: (id: string) => {
      const row = byId.get(id);
      if (live(row)) setDeleting(row);
    },
    extraActions,
  };

  const dialogs = (
    <>
      {editing ? (
        <EditCommentDialog
          initial={editing.body_text}
          onClose={() => setEditing(null)}
          onSave={(markdown) => {
            edit.mutate({ row: editing, markdown });
            setEditing(null);
          }}
        />
      ) : null}
      <AlertDialog open={deleting !== null} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent data-slot="comment-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this message?</AlertDialogTitle>
            <AlertDialogDescription>
              It shows as “This message was deleted.” for everyone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (deleting) remove.mutate(deleting);
                setDeleting(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
  return { listProps, editable, dialogs };
}

function EditCommentDialog({
  initial,
  onSave,
  onClose,
}: {
  initial: string;
  onSave: (markdown: string) => void;
  onClose: () => void;
}) {
  const [text, setText] = useState(initial);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (text.trim()) onSave(text.trim());
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <form onSubmit={submit} className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle>Edit message</DialogTitle>
            <DialogDescription>
              Messages can be edited for 15 minutes after posting.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="edit-task-comment">Message</FieldLabel>
            <Textarea
              id="edit-task-comment"
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </Field>
          <DialogFooter>
            <DialogClose render={<Button variant="ghost" type="button" />}>Cancel</DialogClose>
            <Button type="submit" disabled={!text.trim()}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
